import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import { take } from 'lib/array/arrayUtils'
import type { ComponentProps, PropsWithChildren } from 'react'
import { useMemo } from 'react'
import OfferCard from './OfferCards/OfferCard'
import OfferCardLoadingSkeleton from './OfferCards/OfferCardLoadingSkeleton'
import OfferListOfferCard from './OfferCards/OfferListOfferCard'
import OfferListLoadingOfferCard from './OfferListTiles/OfferListLoadingOfferCard'
import { OFFER_LIST_CAROUSEL_DEFAULT_LIMIT } from './offerListCarouselConstants'
import useOfferListCarouselDisplayLimit from './useOfferListCarouselDisplayLimit'
import useGrowthOfferTile from './OfferCards/GrowthOfferCard/useGrowthOfferTile'

type OfferCardProps = ComponentProps<typeof OfferCard>
type OfferCardVariant = Exclude<OfferCardProps['variant'], undefined>
type OfferCardSize = Exclude<OfferCardProps['size'], undefined>

type TileStyle = OfferCardVariant | 'hero' | 'search' | 'frontpage'

function isOfferCardVariant(tileStyle: TileStyle): tileStyle is OfferCardVariant {
  return tileStyle.startsWith('card')
}

interface Props extends PropsWithChildren {
  inView: boolean
  offerList: App.OfferList
  /**
   * The style of card that'll be displayed
   * @default card-default
   */
  tileStyle?: TileStyle
  tileSize?: OfferCardSize
  /**
   * Pass additional query params to the offer URL generated for each card
   * NOTE: This only works for tileStyle="card" at the moment
   */
  additionalOfferUrlQuery?: string
  filters?: App.OfferListFilters
  /**
   * Number of cards to be shown at max
   * @default OFFER_LIST_CAROUSEL_DEFAULT_LIMIT
   */
  limit?: number
  /**
   * @default limit
   */
  tabletLimit?: number | 'default'
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  offerDistances?: Record<string, number | undefined>
  offerDistanceFrom?: string
  /**
   * Whether or not to tell the offer tiles to eager load their images
   * This is mostly used for when the carousel is part of an SSR and we want them
   * shown before the app mounts.
   */
  eagerLoadImages?: boolean
  growthTileNoBorder?: boolean
}

function OfferListCarouselTrack({
  children,
  inView,
  offerList,
  tileStyle = 'card-default',
  tileSize = 'small',
  additionalOfferUrlQuery,
  filters,
  limit = OFFER_LIST_CAROUSEL_DEFAULT_LIMIT,
  tabletLimit = limit,
  selectedDatesAndRoomsFilter,
  eagerLoadImages,
  offerDistances,
  offerDistanceFrom,
  growthTileNoBorder,
}: Props) {
  const isGrowthTile = useGrowthOfferTile(isOfferCardVariant(tileStyle))
  const growthTileClassName = growthTileNoBorder && isGrowthTile ? 'growth-tile-no-border' : undefined
  const loadingTileSize = isGrowthTile ? 'small' : tileSize
  const listLimit = useOfferListCarouselDisplayLimit(limit, tabletLimit)

  const offerIds = useMemo(() => take(offerList.offerIds ?? [], listLimit), [listLimit, offerList.offerIds])

  const isCardStyle = isOfferCardVariant(tileStyle)

  return <LoaderPlayStateContext.Provider value={inView ? 'running' : 'paused'}>
    {isCardStyle && <>
      {offerList.fetching && <>
        <OfferCardLoadingSkeleton growthTile={isGrowthTile} variant={tileStyle} size={loadingTileSize} className={growthTileClassName} />
        <OfferCardLoadingSkeleton growthTile={isGrowthTile} variant={tileStyle} size={loadingTileSize} className={growthTileClassName} />
        <OfferCardLoadingSkeleton growthTile={isGrowthTile} variant={tileStyle} size={loadingTileSize} className={growthTileClassName} />
        <OfferCardLoadingSkeleton growthTile={isGrowthTile} variant={tileStyle} size={loadingTileSize} className={growthTileClassName} />
      </>}
      {offerIds.map((id, index) => (
        <OfferCard
          variant={tileStyle}
          key={id}
          id={id}
          className={growthTileClassName}
          idx={index}
          // Fetch the first two offers before visibility; defer later offer fetches until in view.
          lazyLoad={index > 1}
          size={tileSize}
          additionalOfferUrlQuery={additionalOfferUrlQuery}
          filters={filters}
          selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
          eagerLoadImage={index < 4 && eagerLoadImages}
          listKey={offerList.key}
          distance={offerDistances?.[id]}
          distanceFrom={offerDistanceFrom}
        />
      ))}
    </>}
    {!isCardStyle && <>
      {offerList.fetching && <>
        <OfferListLoadingOfferCard tileStyle={tileStyle} />
        <OfferListLoadingOfferCard tileStyle={tileStyle} />
      </>}
      {offerIds.map((id, index) => <OfferListOfferCard
        key={id}
        offerId={id}
        tileStyle={tileStyle}
        position={index}
        eagerLoadImage={index < 4 && eagerLoadImages}
      />)}
    </>}
    {children}
  </LoaderPlayStateContext.Provider>
}

export default OfferListCarouselTrack
